// apps/web/src/features/clients/components/ClientVersionsPanel.tsx
//
// Version history list — metadata only (PII-free per the issue body's
// resolved-decisions). Each row opens `ClientVersionRestoreModal` which
// fetches the full snapshot on demand. Version history is unbounded; the
// panel uses cursor pagination with a "Load more" CTA so the user can
// reach versions past the backend's default page size.
//
// INVARIANT (AC-5): the history is NOT fetched with the page. It loads on the
// first deliberate open — most visits to a client are about calling someone or
// copying requisites, and an unbounded audit list has no place in that path.
// Passing `null` as the client id keeps the query disabled until then.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientVersionAction, type ClientVersionListItem } from '@magimp/shared';
import { useClientVersionsInfinite } from '@magimp/shared-hooks';
import { Button, ErrorState, LoadingState, SectionCard } from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useTouchActionStyle, useTouchControlHeightScopeStyle } from '../useMobileFieldStyle.js';
import { ClientVersionRestoreModal } from './ClientVersionRestoreModal.js';

const ACTION_LABEL_KEY = {
  [ClientVersionAction.CLIENT_CREATE]: 'clients:versionAction.create',
  [ClientVersionAction.CLIENT_UPDATE]: 'clients:versionAction.update',
  [ClientVersionAction.CLIENT_ARCHIVE]: 'clients:versionAction.archive',
  [ClientVersionAction.CLIENT_RESTORE]: 'clients:versionAction.restore',
  [ClientVersionAction.CLIENT_VERSION_RESTORE]: 'clients:versionAction.versionRestore',
  [ClientVersionAction.CLIENT_CONTACT_ADD]: 'clients:versionAction.contactAdd',
  [ClientVersionAction.CLIENT_CONTACT_UPDATE]: 'clients:versionAction.contactUpdate',
  [ClientVersionAction.CLIENT_CONTACT_ARCHIVE]: 'clients:versionAction.contactArchive',
  [ClientVersionAction.CLIENT_CONTACT_RESTORE]: 'clients:versionAction.contactRestore',
  [ClientVersionAction.CLIENT_CONTACT_SET_PRIMARY]: 'clients:versionAction.contactPrimary',
  [ClientVersionAction.CLIENT_LEGAL_ADD]: 'clients:versionAction.legalAdd',
  [ClientVersionAction.CLIENT_LEGAL_UPDATE]: 'clients:versionAction.legalUpdate',
  [ClientVersionAction.CLIENT_LEGAL_ARCHIVE]: 'clients:versionAction.legalArchive',
  [ClientVersionAction.CLIENT_LEGAL_RESTORE]: 'clients:versionAction.legalRestore',
  [ClientVersionAction.CLIENT_LIFECYCLE_WORKING]: 'clients:versionAction.lifecycleWorking',
} as const;

export interface ClientVersionsPanelProps {
  clientId: string;
  clientName: string;
  /** False for read-only actors and while the live aggregate is archived. */
  canManage: boolean;
}

export function ClientVersionsPanel({ clientId, clientName, canManage }: ClientVersionsPanelProps) {
  const { t, i18n } = useTranslation(['clients', 'common']);
  const actionStyle = useTouchActionStyle();
  const errorScopeStyle = useTouchControlHeightScopeStyle();
  const [opened, setOpened] = useState(false);
  const versionsQ = useClientVersionsInfinite(opened ? clientId : null);
  const [restoreTarget, setRestoreTarget] = useState<ClientVersionListItem | null>(null);

  const items: ClientVersionListItem[] = useMemo(
    () => (versionsQ.data?.pages ?? []).flatMap((p) => p.items),
    [versionsQ.data],
  );

  const fmt = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <SectionCard
      title={t('clients:sections.versions')}
      action={
        <Button
          kind="quiet"
          size="sm"
          style={actionStyle}
          aria-expanded={opened}
          onClick={() => setOpened((v) => !v)}
          data-testid="client-versions-toggle"
        >
          {opened ? t('clients:actions.hideHistory') : t('clients:actions.showHistory')}
        </Button>
      }
    >
      {!opened ? (
        <p style={{ color: 'var(--fg-3)', margin: 0 }}>{t('clients:versionsCollapsed')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
          {versionsQ.isPending && <LoadingState />}
          {versionsQ.error && (
            <div style={errorScopeStyle}>
              <ErrorState
                title={t('common:states.loadError')}
                text={errorMessage(versionsQ.error)}
                onRetry={() => void versionsQ.refetch()}
              />
            </div>
          )}
          {versionsQ.isSuccess && items.length === 0 && (
            <p style={{ color: 'var(--fg-3)', margin: 0 }}>{t('clients:emptyVersions')}</p>
          )}
          {versionsQ.isSuccess && items.length > 0 && (
            <>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--s-2)',
                }}
              >
                {items.map((v) => (
                  <li
                    key={v.id}
                    style={{
                      display: 'flex',
                      gap: 'var(--s-3)',
                      alignItems: 'center',
                      padding: 'var(--s-2) var(--s-4)',
                      border: '1px solid var(--border-1)',
                      borderRadius: 'var(--r-2)',
                    }}
                  >
                    <span style={{ fontWeight: 500, minWidth: 36 }}>#{v.versionNumber}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <div>{t(ACTION_LABEL_KEY[v.action])}</div>
                      <div style={{ fontSize: 'var(--fz-12)', color: 'var(--fg-3)' }}>
                        {fmt.format(new Date(v.createdAt))}
                        {v.actor ? ` · ${v.actor.name}` : ''}
                      </div>
                    </span>
                    <Button
                      kind="ghost"
                      size="sm"
                      style={actionStyle}
                      onClick={() => setRestoreTarget(v)}
                      aria-label={t('clients:actions.viewVersionOf', {
                        number: v.versionNumber,
                        name: clientName,
                      })}
                    >
                      {t('clients:actions.viewVersion')}
                    </Button>
                  </li>
                ))}
              </ul>
              {versionsQ.hasNextPage && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s-2)' }}>
                  <Button
                    style={actionStyle}
                    onClick={() => {
                      void versionsQ.fetchNextPage();
                    }}
                    disabled={versionsQ.isFetchingNextPage}
                    data-testid="client-versions-load-more"
                  >
                    {versionsQ.isFetchingNextPage
                      ? t('common:states.loading')
                      : t('clients:actions.loadMore')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {restoreTarget && (
        <ClientVersionRestoreModal
          clientId={clientId}
          clientName={clientName}
          version={restoreTarget}
          canManage={canManage}
          onClose={() => setRestoreTarget(null)}
        />
      )}
    </SectionCard>
  );
}
